// Opener — the data-grounded first-open state (ledger row 2 / issue #20).
//
// Pure presentational: takes the useOpenerData payload via props so every
// state is unit-testable and kitchen-sinkable. Copy is forward-framed
// (no-ambush: "ready for review", never failure counts). Starters are
// ThreadPrimitive.Suggestion — a tap sends a REAL user turn; only then
// does the LLM engage.
import React from 'react';
import { ThreadPrimitive } from '@assistant-ui/react';

function Starter({ prompt, label }) {
  return (
    <ThreadPrimitive.Suggestion prompt={prompt} method="replace" autoSend asChild>
      <button type="button" className="starter">
        <span>{label}</span>
        <span className="arrow">→</span>
      </button>
    </ThreadPrimitive.Suggestion>
  );
}

export default function Opener({ data }) {
  if (!data || data.status === 'loading') {
    return <p className="text-sm text-faint px-4 py-3">Opening the chat…</p>;
  }

  if (data.status === 'new-user') {
    return (
      <div className="px-4 py-3">
        <span className="turn-eyebrow">tutor</span>
        <p className="max-w-[66ch] text-[14.5px] leading-[1.62] text-read">
          Welcome. Tell me what you want to learn — anything — and I’ll
          sketch a course for it.
        </p>
        <div className="starters">
          <Starter prompt="I want to learn something new — help me get started." label="Start my first course" />
        </div>
      </div>
    );
  }

  if (data.status === 'ready') {
    const { course, dueCount, weakest, pendingDrafts } = data;
    const draft = pendingDrafts?.count > 0 ? pendingDrafts.latest : null;
    const draftHours = draft ? Math.max(1, Math.ceil(draft.expires_in_minutes / 60)) : 0;
    return (
      <div className="px-4 py-3">
        <span className="turn-eyebrow">tutor</span>
        <p className="max-w-[66ch] text-[14.5px] leading-[1.62] text-read">
          Welcome back.{' '}
          {dueCount > 0
            ? <>In <b className="text-ink">{course.name}</b>, <b className="text-ink">{dueCount} concept{dueCount === 1 ? ' is' : 's are'}</b> ready for review.</>
            : <>You’re all caught up in <b className="text-ink">{course.name}</b>.</>}
          {weakest && <> Strengthening <span className="hl">{weakest.concept}</span> would unlock the most right now.</>}
        </p>
        {draft && (
          <p className="max-w-[66ch] text-[14.5px] leading-[1.62] text-read">
            You left a draft on <b className="text-ink">{draft.concept}</b> — it expires in {draftHours}h.
          </p>
        )}
        <div className="starters">
          {draft && (
            <Starter
              prompt={`Show me the draft I left on ${draft.concept}.`}
              label="Show me that draft"
            />
          )}
          {dueCount > 0 && (
            <Starter
              prompt={`Let's review what's due in ${course.code}.`}
              label={`Review the ${dueCount} due concept${dueCount === 1 ? '' : 's'}`}
            />
          )}
          {weakest && (
            <Starter
              prompt={`Help me with ${weakest.concept} in ${course.code} — it keeps tripping me up.`}
              label={`Tackle ${weakest.concept} head-on`}
            />
          )}
          {!dueCount && !weakest && (
            <Starter prompt="What should I focus on today?" label="What should I focus on?" />
          )}
        </div>
      </div>
    );
  }

  // fallback — the old generic greeting, never an error surface
  return (
    <p className="text-sm text-mutedink px-4 py-3">
      Hey — what do you want to dig into today?
    </p>
  );
}
